import {
  type CSSProperties,
  type ExtractPropTypes,
  type PropType,
  type TeleportProps,
  defineComponent,
  nextTick,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'

// Utils

import {
  HAPTICS_FEEDBACK,
  callInterceptor,
  createNamespace,
  makeArrayProp,
  makeNumericProp,
  makeStringProp,
  pick,
  truthProp,
  unknownProp,
  windowHeight,
  windowWidth,
} from '@xzx-design/utils'

import { useExpose, useRect } from '@xzx-design/hooks'

import { Icon, Popup, Swipe } from '@xzx-design/components'
import ImagePreviewItem from './image-preview-item'

// Types
import type {
  ImagePreviewItemInstance,
  ImagePreviewScaleEventParams,
} from './types'

import type {
  PopupCloseIconPosition,
  SwipeInstance,
  SwipeToOptions,
} from '@xzx-design/components'

import type { Interceptor } from '@xzx-design/utils'

const [name, bem] = createNamespace('image-preview')

const popupProps = [
  'show',
  'teleport',
  'transition',
  'overlayStyle',
  'closeOnPopstate',
] as const

export const imagePreviewProps = {
  show: Boolean,
  loop: truthProp,
  images: makeArrayProp<string>(),
  minZoom: makeNumericProp(1 / 3),
  maxZoom: makeNumericProp(3),
  overlay: truthProp,
  vertical: Boolean,
  closeable: Boolean,
  showIndex: truthProp,
  className: unknownProp,
  closeIcon: makeStringProp('clear'),
  transition: String,
  beforeClose: Function as PropType<Interceptor>,
  doubleScale: truthProp,
  overlayClass: unknownProp,
  overlayStyle: Object as PropType<CSSProperties>,
  swipeDuration: makeNumericProp(300),
  startPosition: makeNumericProp(0),
  showIndicators: Boolean,
  closeOnPopstate: truthProp,
  closeOnClickImage: truthProp,
  closeOnClickOverlay: truthProp,
  closeIconPosition: makeStringProp<PopupCloseIconPosition>('top-right'),
  teleport: [String, Object] as PropType<TeleportProps['to']>,
}

export type ImagePreviewProps = ExtractPropTypes<typeof imagePreviewProps>

export default defineComponent({
  name,

  props: imagePreviewProps,

  emits: ['scale', 'close', 'closed', 'change', 'longPress', 'update:show'],

  setup(props, { emit, slots }) {
    const swipeRef = ref<SwipeInstance>()
    const activedPreviewItemRef = ref<ImagePreviewItemInstance>()

    const state = reactive({
      active: 0,
      rootWidth: 0,
      rootHeight: 0,
      disableZoom: false,
    })

    const resize = () => {
      if (swipeRef.value) {
        const rect = useRect(swipeRef.value.$el)
        state.rootWidth = rect.width
        state.rootHeight = rect.height
        swipeRef.value.resize()
      }
    }

    const emitScale = (args: ImagePreviewScaleEventParams) =>
      emit('scale', args)

    const updateShow = (show: boolean) => emit('update:show', show)

    const emitClose = () => {
      callInterceptor(props.beforeClose, {
        args: [state.active],
        done: () => updateShow(false),
      })
    }

    const setActive = (active: number) => {
      if (active !== state.active) {
        state.active = active
        emit('change', active)
      }
    }

    const renderIndex = () => {
      if (props.showIndex) {
        return (
          <div class={bem('index')}>
            {slots.index
              ? slots.index({ index: state.active })
              : `${state.active + 1} / ${props.images.length}`}
          </div>
        )
      }
    }

    const renderCover = () => {
      if (slots.cover) {
        return <div class={bem('cover')}>{slots.cover()}</div>
      }
    }

    const onDragStart = () => {
      state.disableZoom = true
    }

    const onDragEnd = () => {
      state.disableZoom = false
    }

    const renderImages = () => (
      <Swipe
        ref={swipeRef}
        lazyRender
        loop={props.loop}
        class={bem('swipe')}
        vertical={props.vertical}
        duration={props.swipeDuration}
        initialSwipe={props.startPosition}
        showIndicators={props.showIndicators}
        indicatorColor="white"
        onChange={setActive}
        onDragEnd={onDragEnd}
        onDragStart={onDragStart}
      >
        {props.images.map((image, index) => (
          <ImagePreviewItem
            v-slots={{
              image: slots.image,
            }}
            ref={(item: ImagePreviewItemInstance) => {
              if (index === state.active) {
                activedPreviewItemRef.value = item
              }
            }}
            src={image}
            show={props.show}
            active={state.active}
            maxZoom={props.maxZoom}
            minZoom={props.minZoom}
            rootWidth={state.rootWidth}
            rootHeight={state.rootHeight}
            disableZoom={state.disableZoom}
            doubleScale={props.doubleScale}
            closeOnClickImage={props.closeOnClickImage}
            closeOnClickOverlay={props.closeOnClickOverlay}
            vertical={props.vertical}
            onScale={emitScale}
            onClose={emitClose}
            onLongPress={() => emit('longPress', { index })}
          />
        ))}
      </Swipe>
    )

    const renderClose = () => {
      if (props.closeable) {
        return (
          <Icon
            role="button"
            name={props.closeIcon}
            class={[
              bem('close-icon', props.closeIconPosition),
              HAPTICS_FEEDBACK,
            ]}
            onClick={emitClose}
          />
        )
      }
    }

    const onClosed = () => emit('closed')

    const swipeTo = (index: number, options?: SwipeToOptions) =>
      swipeRef.value?.swipeTo(index, options)

    useExpose({
      resetScale: () => {
        activedPreviewItemRef.value?.resetScale()
      },
      swipeTo,
    })

    onMounted(resize)

    watch([windowWidth, windowHeight], resize)

    watch(
      () => props.startPosition,
      (value) => setActive(+value)
    )

    watch(
      () => props.show,
      (value) => {
        const { images, startPosition } = props
        if (value) {
          setActive(+startPosition)
          nextTick(() => {
            resize()
            swipeTo(+startPosition, { immediate: true })
          })
        } else {
          emit('close', {
            index: state.active,
            url: images[state.active],
          })
        }
      }
    )

    return () => (
      <Popup
        class={[bem(), props.className]}
        overlayClass={[bem('overlay'), props.overlayClass]}
        onClosed={onClosed}
        onUpdate:show={updateShow}
        {...pick(props, popupProps)}
      >
        {renderClose()}
        {renderImages()}
        {renderIndex()}
        {renderCover()}
      </Popup>
    )
  },
})
